import React from 'react';
import { graphql, Link } from 'gatsby';
import styled from 'styled-components';
import { TransitionGroup } from 'react-transition-group';
import PropTypes from 'prop-types';

import Home from '../components/Home';
import SEO from '../components/Seo';
import Logo from '../components/Logo';
import Card from '../components/Card';
import Button from '../components/Button';
import { T5D } from '../components/Text';
import LogoContainer from '../components/Containers/LogoContainer';
import PostCardContainer from '../components/Containers/PostCardContainer';
import Footer from '../components/Footer';

import GlobalStyle from '../styles/global';
import { getURL } from '../helpers/url';
import { colors, media } from '../styles/constants';
import SpLogo from '../assets/svg/splogo.svg';
import { onlyHorizontalPaddingStyle } from '../styles/padding';

const Container = styled.div`
  ${T5D} {
    margin-bottom: 3rem;
    max-width: 97rem;
    color: ${colors.white};

    @media (min-width: ${media.tabletPortrait}) {
      margin-bottom: 4rem;
    }

    @media (min-width: ${media.bigDesktop}) {
      margin-bottom: 6rem;
    }
  }

  ${PostCardContainer} {
    margin-bottom: 1rem;
  }

  ${Button} {
    margin-top: 1.5rem;
    margin-bottom: 15rem;

    @media (min-width: ${media.tabletPortrait}) {
      margin-top: 0rem;
      margin-bottom: 10rem;
    }

    @media (min-width: ${media.tabletLandscape}) {
      margin-top: 2rem;
    }

    @media (min-width: ${media.desktop}) {
      margin-bottom: 3rem;
      margin-top: 2rem;
    }

    @media (min-width: ${media.bigDesktop}) {
      margin-top: 3rem;
    }
  }
`;

const IntroContainer = styled(Container)`
  ${T5D} {
    ${onlyHorizontalPaddingStyle}
  }

  svg {
    width: 90%;
    margin-top: 1rem;
    min-width: 33rem;
    min-height: 18rem;

    @media (min-width: ${media.phone}) {
      min-width: 38rem;
    }

    @media (min-width: ${media.tabletPortrait}) {
      min-height: 36rem;
      min-width: 77rem;
      max-width: 85rem;
    }

    @media (min-width: ${media.desktop}) {
      min-height: 40rem;
      min-width: 82.3rem;
      max-width: 83rem;
    }

    @media (min-width: ${media.bigDesktop}) {
      min-height: 50rem;
      min-width: 120rem;
    }
  }
`;

const IndexPage = ({
  location: { pathname },
  data: { strapiHomepage: page, allStrapiPost: posts }
}) => (
  <>
    <SEO title="NYC-Based Mobile App Studio" description="Small Planet creates award-winning mobile apps and digital products, offering UX, design, and development services for leading companies and startups.
" pathname={pathname} />
    <GlobalStyle />
    <Home>
      <IntroContainer>
        <T5D>{page.headline}</T5D>
        <SpLogo />
      </IntroContainer>
      <Container>
        <T5D as="h2">We work for some amazing people.</T5D>
        <TransitionGroup component={LogoContainer}>
          {page.clients.slice(0, 10).map(client => (
            <Logo
              key={client.id}
              imgSrc={client.whitelogo.publicURL}
              imgAlt={`${client.name} logo`}
              center
            />
          ))}
        </TransitionGroup>
        <Button>
          <Link to="/work">See All Clients</Link>
        </Button>
      </Container>
      <Container>
        <T5D as="h2">Check us out.</T5D>
        <TransitionGroup component={PostCardContainer}>
          {posts.edges.map(post => (
            <Card
              key={post.node.id}
              imgSrc={post.node.image}
              imgAlt={post.node.title}
              title={post.node.title}
              text={post.node.shortsubtitle}
              tags={post.node.tags}
              link={getURL('observatory', post.node.title)}
            />
          ))}
        </TransitionGroup>
        <Button>
          <Link to="/observatory">See All Posts</Link>
        </Button>
      </Container>
      <Footer isInView renderCircle={false} />
    </Home>
  </>
);

IndexPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default IndexPage;

export const pageQuery = graphql`
  query IndexPage($publishedFilter: [Boolean]) {
    strapiHomepage(name: { eq: "Home" }) {
      headline
      clients {
        id
        name
        whitelogo {
          publicURL
        }
      }
    }
    allStrapiPost(
      limit: 4
      sort: { fields: [date], order: DESC }
      filter: { published: { in: $publishedFilter } }
    ) {
      edges {
        node {
          id
          title
          shortsubtitle
          tags {
            name
          }
          image {
            publicURL
            childImageSharp {
              fluid(
                maxWidth: 700
                maxHeight: 440
                cropFocus: CENTER
                quality: 75
              ) {
                ...GatsbyImageSharpFluid_noBase64
              }
            }
          }
        }
      }
    }
  }
`;
